<template>
  <div :class="['perk-container margin-bottom-xxs padding-xs', { 'perk-container--redesign': isProductPerksEnabledPhase2 }]">
    <slot name="icon">
      <img v-if="dynamicIcon" :src="dynamicIcon" :alt="props.iconAlt" class="icon icon-left" />
      <span v-else :class="icon" class="icon icon-left"></span>
    </slot>
    <div class="perk">
      <slot></slot>
    </div>
    <slot name="tooltip">
      <i
        :class="[
          'tooltip icon',
          {
            'tool-tip-dark-medium': isProductPerksEnabled,
            'chevron-large-right-dark-small': isProductPerksEnabledPhase2,
          },
        ]"
      ></i>
    </slot>
    <component
      :is="url ? 'a' : 'button'"
      v-if="isRawClickable"
      class="stretched"
      v-bind="customAttributes"
      :class="{ [klarnaConfig.customButtonClass]: isKlarna }"
      @click="emit('value-info-click', type)"
    />
  </div>
</template>
<script setup>
import { computed } from 'vue';

import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import LoyallistImage from '@core/csg/src/assets/images/bcom/compressed/loyalty-bloom-loyallist-small.svg';
import LoyaltyRed from '@core/csg/src/assets/images/mcom/compressed/loyalty-macys-red-large.svg';
import { klarna as klarnaConfig } from 'config';

const props = defineProps({
  icon: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  url: {
    type: String,
    default: '',
  },
  isRawClickable: {
    type: Boolean,
    default: false,
  },
  isKlarna: {
    type: Boolean,
    default: false,
  },
  iconAlt: {
    type: String,
    default: 'loyallist icon',
  },
  screenReaderLabel: {
    type: String,
    default: 'View offer details',
  },
});
const emit = defineEmits(['value-info-click']);
const { isProductPerksEnabled, isProductPerksEnabledPhase2 } = useFeatureEligibility();

const customAttributes = props.url
  ? {
      href: props.url,
      target: '_blank',
    }
  : {
      'aria-label': props.screenReaderLabel,
    };

const dynamicIconMap = {
  'star-rewards-red-large-2D': LoyaltyRed,
  'loyallist-small': LoyallistImage,
};
const dynamicIcon = computed(() => dynamicIconMap[props.icon]);
</script>

<style lang="scss">
@import '~settingSCSS';
@import '~@csgIcons/new/tool-tip-dark-medium';
@import '~@csgIcons/new/financing-dark-medium';
@import '~@csgIcons/new/truck-dark-medium';
@import '~@csgIcons/new/personalized-offers-dark-medium';
@import '~@csgIcons/new/chevron-large-right-dark-small';

.perk-container {
  position: relative;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  background-color: $background-color;
  text-decoration: none;

  .icon {
    min-width: rem-calc(24);
    display: flex;

    &.icon-left {
      margin-right: rem-calc(12);
    }
  }

  .tooltip {
    margin-left: auto;
    cursor: pointer;
    flex-shrink: 0;
  }
}

.stretched {
  position: absolute;
  inset: 0;
  z-index: 1;
  border: 0;
  padding: 0;
  background: transparent;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
}

.special {
  background-color: #f6d96a;
}
.perk :is(a, button, [role='button'], [role='link']) {
  position: relative;
  z-index: 2;
}

.perk {
  overflow: hidden;
}

.perk-container.perk-container--redesign {
  @include margin-bottom-z;
  @include padding-top-xs;
  @include padding-bottom-xs;
  @include padding-left-z;
  @include padding-right-z;
  border-radius: 0;

  &:hover {
    background-color: $hover-color;

    .klarna-perks {
      ::part(osm-container) {
        background-color: $hover-color;
      }
    }
  }

  &:not(:first-child) {
    position: relative;
    &:before {
      position: absolute;
      content: '';
      top: -1px;
      left: 52px;
      width: calc(100% - 52px);
      height: 0.5px;
      background-color: $gray-2-color;
    }
  }

  .icon {
    min-width: auto;
    &.icon-left {
      min-width: rem-calc(24);
    }
  }
}
</style>

<style lang="scss" brand="mcom">
@import '~@csgIcons/new/registry-dark-medium';

.perk-container {
  border-radius: rem-calc(5);

  &.perk-container--redesign {
    @include padding-left-xs;

    .icon {
      margin-right: rem-calc(12px);
    }
  }
}
</style>

<style lang="scss" brand="bcom">
@import '~@csgIcons/new/gift-wrap-dark-medium';

.perk-container {
  &.perk-container--redesign {
    background-color: $white;

    &:not(:first-child) {
      &:before {
        left: 36px;
        width: calc(100% - 36px);
      }
    }
  }
}
</style>
